import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  getAdditionalUserInfo,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getAnonymousUserId } from '../utils/anonymousUser';
import { installAccountDeletionGuard } from '../utils/accountDeletionGuard';
// 마이그레이션 관련 import 제거
import { ensureUserInitialized } from '../utils/userUtils';
import { createInAppBrowserAuthError, isEmbeddedWebView } from '../utils/inAppBrowser';

// 인증 컨텍스트 생성
const AuthContext = createContext();

// 인증 컨텍스트 프로바이더 컴포넌트
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userInitialization, setUserInitialization] = useState({
    uid: null,
    status: 'idle',
  });
  // 익명 사용자 상태 추가
  const [isAnonymous, setIsAnonymous] = useState(false);
  const initializedUserUidRef = useRef(null);
  const authGenerationRef = useRef(0);
  const initializationPromisesRef = useRef(new Map());
  // 마이그레이션 상태 제거

  // 마이그레이션 함수 제거 // migrationState.inProgress만 의존성으로 추가

  useEffect(() => {
    let mounted = true;

    // 일반 익명 사용자 처리 함수
    const handleAnonymousUser = () => {
      const anonymousUserId = getAnonymousUserId();
      setIsAnonymous(true);

      // 익명 사용자 정보 설정
      setUserDetails({
        uid: anonymousUserId,
        displayName: '익명 사용자',
        isAnonymous: true,
        role: 'anonymous'
      });
    };

    const isLatestAuth = (generation, uid, isTempReviewUser = false) => (
      mounted
      && authGenerationRef.current === generation
      && (
        auth.currentUser?.uid === uid
        || (!auth.currentUser && isTempReviewUser)
      )
    );

    const initializeUser = async (user, generation) => {
      if (initializedUserUidRef.current === user.uid) {
        if (isLatestAuth(generation, user.uid, user.isTempReviewUser)) {
          setUserInitialization({ uid: user.uid, status: 'ready' });
        }
        return { success: true };
      }

      if (isLatestAuth(generation, user.uid, user.isTempReviewUser)) {
        setUserInitialization({ uid: user.uid, status: 'loading' });
      }

      let promise = initializationPromisesRef.current.get(user.uid);
      if (!promise) {
        promise = (async () => {
          let result = await ensureUserInitialized(user);
          if (!result.success) {
            await new Promise((resolve) => setTimeout(resolve, 750));
            result = await ensureUserInitialized(user);
          }
          return result;
        })();
        initializationPromisesRef.current.set(user.uid, promise);
        promise.finally(() => {
          if (initializationPromisesRef.current.get(user.uid) === promise) {
            initializationPromisesRef.current.delete(user.uid);
          }
        });
      }

      const result = await promise;
      if (!isLatestAuth(generation, user.uid, user.isTempReviewUser)) return result;

      if (result.success) {
        initializedUserUidRef.current = user.uid;
      } else {
        initializedUserUidRef.current = null;
      }
      setUserInitialization({
        uid: user.uid,
        status: result.success ? 'ready' : 'failed',
      });
      return result;
    };

    // Firebase 인증 상태 변경 리스너
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const generation = authGenerationRef.current + 1;
      authGenerationRef.current = generation;
      setCurrentUser(user);
      
      if (user) {
        // Firebase 인증된 사용자
        setIsAnonymous(false);

        // Meta Pixel 고급 매칭(Advanced Matching) — 로그인 시 사용자 데이터 전달
        if (typeof window.fbq === 'function' && user.email) {
          window.fbq('init', '958151393232381', { em: user.email, external_id: user.uid });
        }

        // Firestore 데이터베이스 접근 오류 회피를 위해 임시적으로 Auth 정보만 사용
        // Firebase Auth에서 제공하는 기본 정보를 사용
        setUserDetails({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '사용자',
          photoURL: user.photoURL,
          role: 'basic' // 기본 역할 설정
        });
        
        // 🚀 신규/기존 사용자 자동 초기화 API 호출
        await initializeUser(user, generation);
        
        /* 
        // 기존 Firestore 조회 코드 (임시 주석 처리)
        try {
          // Firestore에서 추가 사용자 정보 가져오기
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserDetails(userDoc.data());
          } else {
            setUserDetails(null);
          }
        } catch (error) {
          console.error("사용자 정보 조회 오류:", error);
          setUserDetails(null);
        }
        */
      } else {
        // Firebase 인증되지 않은 사용자
        
        // 토스 심사용 임시 사용자 확인
        const tempUserData = localStorage.getItem('temp_toss_user');
        if (tempUserData) {
          try {
            const tempUser = JSON.parse(tempUserData);
            const tempUserWithFlag = { ...tempUser, isTempReviewUser: true };
            
            // 임시 사용자로 설정
            setCurrentUser(prev => (
              prev?.isTempReviewUser && prev?.uid === tempUserWithFlag.uid
                ? prev
                : tempUserWithFlag
            ));
            setIsAnonymous(false);
            
            // Free 계정으로 설정 (Pro 구매 테스트를 위해)
            setUserDetails({
              uid: tempUser.uid,
              email: tempUser.email,
              displayName: tempUser.displayName,
              photoURL: tempUser.photoURL,
              role: 'basic', // Free 계정
              subscription: {
                type: 'free',
                status: 'active',
                plan_id: 'free'
              },
              tokens: {
                current: 10, // Free 계정 기본 토큰
                daily_limit: 10
              },
              isTempReviewUser: true
            });
            
            // 🚀 임시 사용자도 백엔드 초기화 API 호출
            await initializeUser(tempUserWithFlag, generation);
          } catch (error) {
            console.error('임시 사용자 데이터 파싱 오류:', error);
            try {
              localStorage.removeItem('temp_toss_user');
            } catch (_storageError) {
              // 저장소 접근이 막혀도 익명 흐름은 계속한다.
            }
            initializedUserUidRef.current = null;
            setUserInitialization({ uid: null, status: 'idle' });
            // 일반 익명 사용자로 처리
            handleAnonymousUser();
          }
        } else {
          initializedUserUidRef.current = null;
          setUserInitialization({ uid: null, status: 'idle' });
          // 일반 익명 사용자 처리
          handleAnonymousUser();
        }
      }
      if (mounted && authGenerationRef.current === generation) {
        setLoading(false);
      }
    });

    const retryInitializationWhenOnline = () => {
      const user = auth.currentUser;
      if (!user || initializedUserUidRef.current === user.uid) return;
      const generation = authGenerationRef.current + 1;
      authGenerationRef.current = generation;
      initializeUser(user, generation);
    };
    window.addEventListener('online', retryInitializationWhenOnline);

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      mounted = false;
      authGenerationRef.current += 1;
      window.removeEventListener('online', retryInitializationWhenOnline);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // 로그인 성공 후 저장된 URL로 리다이렉트 (생기부 페이지 등)
  const handleSavedRedirect = () => {
    const savedData = localStorage.getItem('saenggibu_temp_data');
    if (!savedData) return;
    try {
      const parsedData = JSON.parse(savedData);
      if (parsedData.currentUrl && parsedData.currentUrl.includes('/create-saenggibu')) {
        setTimeout(() => {
          window.location.href = parsedData.currentUrl;
        }, 100);
      }
    } catch (e) {
      console.error('저장된 URL 복원 실패:', e);
    }
  };

  // Google 로그인 함수
  // - 웹(Chrome/Safari) / Android TWA: Firebase JS SDK signInWithPopup
  // - Capacitor 네이티브 앱(iOS/Android Capacitor):
  //   1) Native Google Sign-In SDK로 idToken 획득
  //   2) idToken을 Firebase JS SDK에 전달하여 web layer까지 동기화 (Firestore/Storage 사용 위함)
  //
  // ※ TWA는 Chrome 셸이므로 popup 방식 정상 동작 → isCapacitorNative()로 엄격 분기
  const signInWithGoogle = async () => {
    try {
      const { isCapacitorNative } = await import('../utils/isInApp');

      if (isCapacitorNative()) {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithGoogle({
          skipNativeAuth: true,
        });
        const idToken = result.credential?.idToken;
        if (!idToken) {
          throw new Error('Google 로그인 실패: idToken을 받지 못했습니다.');
        }
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(auth, credential);
        handleSavedRedirect();
        return {
          user: userCredential.user,
          isNewUser: Boolean(
            getAdditionalUserInfo(userCredential)?.isNewUser
          ),
        };
      }

      if (isEmbeddedWebView()) {
        throw createInAppBrowserAuthError();
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      handleSavedRedirect();
      return {
        user: result.user,
        isNewUser: Boolean(getAdditionalUserInfo(result)?.isNewUser),
      };
    } catch (error) {
      console.error('Google 로그인 오류:', error);
      throw error;
    }
  };

  // 로그아웃 함수
  // Capacitor 네이티브 앱: native Firebase 세션도 같이 정리 (TWA 제외)
  const logout = async () => {
    try {
      try {
        const { isCapacitorNative } = await import('../utils/isInApp');
        if (isCapacitorNative()) {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          await FirebaseAuthentication.signOut();
        }
      } catch (e) {
        console.warn('네이티브 로그아웃 실패(무시 가능):', e);
      }
      await signOut(auth);
    } catch (error) {
      console.error('로그아웃 오류:', error);
      throw error;
    }
  };

  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const signedInUid = currentUser?.uid || null;
  useEffect(() => {
    if (!signedInUid) return undefined;
    return installAccountDeletionGuard(() => {
      logoutRef.current().catch(() => {});
    });
  }, [signedInUid]);

  const refreshCurrentUserProfile = async () => {
    if (!auth.currentUser) return null;

    await auth.currentUser.reload();
    const refreshedUser = auth.currentUser;
    await refreshedUser.getIdToken(true);

    setCurrentUser(refreshedUser);
    setUserDetails({
      uid: refreshedUser.uid,
      email: refreshedUser.email,
      displayName: refreshedUser.displayName || '사용자',
      photoURL: refreshedUser.photoURL,
      role: userDetails?.role || 'basic',
      ...(userDetails?.subscription ? { subscription: userDetails.subscription } : {}),
      ...(userDetails?.tokens ? { tokens: userDetails.tokens } : {}),
      ...(userDetails?.isTempReviewUser ? { isTempReviewUser: userDetails.isTempReviewUser } : {}),
    });

    return refreshedUser;
  };

  // 컨텍스트에서 제공할 값들
  const value = {
    currentUser,
    userDetails,
    userInitialization,
    loading,
    isAnonymous,
    signInWithGoogle,
    refreshCurrentUserProfile,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// 인증 컨텍스트 사용을 위한 커스텀 훅
export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext; 